/**
 * Композбл embedded-layout для карточек сущностей (НС, Заявка и др.).
 *
 * Убирает дублирование между формами: каждая форма сущности в режиме
 * редактирования на desktop должна раскладываться на весь экран с отступами
 * 50/50/0/40 и подключать блюр-оверлей. Композбл формирует атрибуты для
 * VDialog (content-class и max-width) на основе признака «это редактирование»
 * от формы и breakpoint'а Vuetify.
 *
 * Форма передаёт Ref на признак isEditMode (обычно computed от route.params.id).
 * Композбл сам читает useDisplay().mdAndUp и возвращает:
 *   - isEmbedded:   активен ли embedded-режим (редактирование + desktop)
 *   - contentClass: имя CSS-класса для VDialog content-class (или undefined)
 *   - maxWidth:     значение max-width для VDialog (undefined в embedded / 1000 в обычном)
 */
import { type ComputedRef, type Ref, computed } from 'vue'
import { useDisplay } from 'vuetify'

/**
 * Возвращаемый интерфейс композбла `useFormEmbeddedLayout`.
 * Форма подставляет эти значения в атрибуты `VDialog`:
 * `:content-class="contentClass" :max-width="maxWidth"`.
 */
export interface IUseFormEmbeddedLayoutReturn {
  /** Активен ли embedded-режим (mdAndUp && редактирование). */
  isEmbedded: ComputedRef<boolean>
  /** Имя CSS-класса для content-class `VDialog` (undefined в обычном режиме). */
  contentClass: ComputedRef<string | undefined>
  /** Значение max-width для `VDialog` (undefined в embedded / 1000 в обычном). */
  maxWidth: ComputedRef<number | undefined>
}

/** CSS-класс, который накатывает embedded-layout на `VDialog`. */
export const EMBEDDED_FORM_CONTENT_CLASS = 'entity-form-embedded'

/** Ширина `VDialog` по умолчанию (используется в обычном режиме без embedded). */
export const DEFAULT_FORM_MAX_WIDTH = 1000

/**
 * Рассчитывает параметры `VDialog` для формы сущности в embedded-режиме.
 *
 * Форма передаёт `isEditMode` (обычно `computed(() => !!route.params.id)`).
 * Композбл сам читает breakpoint из `useDisplay()`: на `mdAndUp` + редактирование
 * форма раскладывается в полноэкранный embedded-layout (отступы 50/50/0/40),
 * иначе — обычное модальное окно 1000px.
 *
 * @param isEditMode Реактивный признак «карточка открыта для редактирования».
 * @returns Объект с реактивными `isEmbedded`, `contentClass`, `maxWidth`.
 */
export function useFormEmbeddedLayout(isEditMode: Ref<boolean> | ComputedRef<boolean>): IUseFormEmbeddedLayoutReturn {
  const { mdAndUp } = useDisplay()

  const isEmbedded = computed(() => mdAndUp.value && isEditMode.value)

  const contentClass = computed(() =>
    isEmbedded.value ? EMBEDDED_FORM_CONTENT_CLASS : undefined,
  )

  const maxWidth = computed(() =>
    isEmbedded.value ? undefined : DEFAULT_FORM_MAX_WIDTH,
  )

  return { isEmbedded, contentClass, maxWidth }
}
